import React, { useState, useRef, useEffect, useId, useCallback } from "react";
import api from "../../lib/api";
import { Field, Input, Spinner } from "./primitives";

/**
 * Reusable, India-first structured address editor — PIN-FIRST (founder spec Req-1):
 * the PIN code is asked first; a valid 6-digit PIN auto-fills country/state/district/city and
 * offers the post-office localities as suggestions; every field stays manually editable.
 *
 *   <AddressField value={f} onChange={(patch) => setF((p) => ({ ...p, ...patch }))} />
 *
 * `value` holds the address keys (postal_code, country, state, district, city, locality,
 * address_line1, address_line2, landmark); `onChange(patch)` is a partial merge.
 *
 * `publicMode` — for pre-auth surfaces (registration wizards): the PIN lookup uses the public
 * rate-limited POST /api/public/pincode and the line-1 autocomplete (an authed endpoint) is
 * disabled; everything else behaves identically.
 *
 * Coarse only — no precise GPS is ever captured (the backend stamps a coarse IP audit record on
 * save). Accessible: the line-1 input is a true ARIA combobox over a role=listbox (Arrow/Enter/
 * Escape nav); locality suggestions ride a native <datalist>; keeps the axe gate green.
 */
export function AddressField({ value = {}, onChange = () => {}, disabled = false, publicMode = false }) {
  const v = value || {};
  const set = (k) => (e) => onChange({ [k]: e.target.value });

  const [sugs, setSugs] = useState([]);
  const [open, setOpen] = useState(false);
  const [active, setActive] = useState(-1);
  const [busy, setBusy] = useState(false);
  const [searchError, setSearchError] = useState(false);
  const [status, setStatus] = useState("");
  const [pinError, setPinError] = useState("");
  const [pinBusy, setPinBusy] = useState(false);
  const [localities, setLocalities] = useState([]);
  const timer = useRef(null);
  const blurTimer = useRef(null);
  const ctrl = useRef(null);
  const listId = useId();
  const pinErrId = useId();
  const locListId = useId();
  const optId = (i) => `${listId}-opt-${i}`;

  const search = useCallback((q) => {
    if (publicMode) return; // the autocomplete endpoint is authed — manual entry on public pages
    if (timer.current) clearTimeout(timer.current);
    if (!q || q.trim().length < 3) {
      ctrl.current?.abort();
      setSugs([]); setOpen(false); setActive(-1); setBusy(false); setSearchError(false); setStatus("");
      return;
    }
    timer.current = setTimeout(async () => {
      ctrl.current?.abort();
      const controller = new AbortController();
      ctrl.current = controller;
      setBusy(true); setOpen(true); setActive(-1); setSearchError(false); setStatus("Searching addresses…");
      try {
        const { data } = await api.post("/address/autocomplete", { query: q }, { signal: controller.signal });
        const items = data?.items || [];
        setSugs(items); setActive(-1); setBusy(false);
        setStatus(items.length
          ? `${items.length} address suggestion${items.length === 1 ? "" : "s"} available`
          : "No matching addresses — you can enter the address manually");
      } catch {
        if (controller.signal.aborted) return;
        setSugs([]); setBusy(false); setSearchError(true);
        setStatus("Address search is unavailable — enter the address manually");
      }
    }, 350);
  }, [publicMode]);

  const cancelSearch = () => {
    if (timer.current) clearTimeout(timer.current);
    ctrl.current?.abort();
    setBusy(false);
  };

  const onLine1 = (e) => { onChange({ address_line1: e.target.value }); search(e.target.value); };

  const pick = (s) => {
    onChange({
      address_line1: s.address_line1 || v.address_line1 || "",
      city: s.city || v.city || "",
      district: s.district || v.district || "",
      state: s.state || v.state || "",
      postal_code: s.postal_code || v.postal_code || "",
      country: s.country || v.country || "India",
    });
    cancelSearch();
    setStatus("Address suggestion applied");
    setOpen(false); setSugs([]); setActive(-1);
  };

  const onKeyDown = (e) => {
    if (!open) return;
    if ((e.key === "ArrowDown" || e.key === "ArrowUp") && sugs.length) {
      e.preventDefault();
      setActive((i) => e.key === "ArrowDown"
        ? (i + 1 >= sugs.length ? 0 : i + 1)
        : (i <= 0 ? sugs.length - 1 : i - 1));
    } else if (e.key === "Enter") {
      e.preventDefault(); e.stopPropagation();
      if (active >= 0 && sugs[active]) pick(sugs[active]); else setOpen(false);
    } else if (e.key === "Escape") {
      e.preventDefault(); e.stopPropagation();
      cancelSearch();
      setOpen(false); setActive(-1);
    }
  };

  const lookupPin = async () => {
    const code = (v.postal_code || "").trim();
    if (!/^\d{6}$/.test(code)) return;
    setStatus("Looking up PIN code…"); setPinBusy(true);
    try {
      const { data } = await api.post(publicMode ? "/public/pincode" : "/address/pincode", { code });
      if (data && (data.state || data.city)) {
        onChange({
          city: v.city || data.city || "",
          district: v.district || data.district || "",
          state: data.state || v.state || "",
          country: data.country || v.country || "India",
        });
        const locs = Array.isArray(data.localities) ? data.localities.filter(Boolean) : [];
        setLocalities(locs);
        setStatus(locs.length
          ? `City and state filled — ${locs.length} localities suggested for this PIN`
          : "City and state filled from PIN code");
      } else {
        setLocalities([]);
        setStatus("Couldn't find details for that PIN — enter city and state manually");
      }
    } catch {
      setLocalities([]);
      setStatus("Couldn't look up that PIN — enter city and state manually");
    } finally {
      setPinBusy(false);
    }
  };

  const onPin = (e) => { setPinError(""); onChange({ postal_code: e.target.value.replace(/\D/g, "").slice(0, 6) }); };

  const onPinBlur = () => {
    const code = (v.postal_code || "").trim();
    if (code && !/^\d{6}$/.test(code)) {
      setPinError("Enter a valid 6-digit PIN code");
      setStatus("Enter a valid 6-digit PIN code");
      return;
    }
    lookupPin();
  };

  useEffect(() => {
    if (open && active >= 0) {
      document.getElementById(`${listId}-opt-${active}`)?.scrollIntoView({ block: "nearest" });
    }
  }, [active, open, listId]);

  return (
    <div className="sm:col-span-2 grid sm:grid-cols-2 gap-4">
      <span role="status" aria-live="polite" className="sr-only">{status}</span>

      {/* PIN FIRST (spec Req-1): enter the PIN → country/state/district/city fill themselves */}
      <Field label="PIN code" help="Enter your 6-digit PIN first — it auto-fills city, district & state">
        <div className="relative">
          <Input value={v.postal_code || ""} onChange={onPin} onBlur={onPinBlur}
                 disabled={disabled} inputMode="numeric" pattern="[0-9]{6}" maxLength={6}
                 autoComplete="postal-code" placeholder="e.g. 560001"
                 data-testid="addr-pin"
                 aria-invalid={pinError ? true : undefined}
                 aria-describedby={pinError ? pinErrId : undefined} />
          {pinBusy && <Spinner aria-hidden="true" className="h-4 w-4 absolute right-3 top-1/2 -translate-y-1/2" />}
        </div>
        {pinError && <p id={pinErrId} className="mt-1 text-sm text-critical">{pinError}</p>}
      </Field>
      <Field label="Country">
        <Input value={v.country || "India"} onChange={set("country")} disabled={disabled} autoComplete="country-name" />
      </Field>
      <Field label="State"><Input value={v.state || ""} onChange={set("state")} disabled={disabled} autoComplete="address-level1" data-testid="addr-state" /></Field>
      <Field label="District"><Input value={v.district || ""} onChange={set("district")} disabled={disabled} autoComplete="address-level3" /></Field>
      <Field label="City"><Input value={v.city || ""} onChange={set("city")} disabled={disabled} autoComplete="address-level2" data-testid="addr-city" /></Field>
      <Field label="Locality / area" help="Pick a post-office locality for your PIN, or type your own">
        <Input value={v.locality || ""} onChange={set("locality")} disabled={disabled}
               list={localities.length ? locListId : undefined} placeholder="e.g. Ashok Nagar"
               data-testid="addr-locality" />
        {localities.length > 0 && (
          <datalist id={locListId}>
            {localities.map((l) => <option key={l} value={l} />)}
          </datalist>
        )}
      </Field>

      {/* then the house/street details (spec: PIN → confirm → house/building) */}
      <div
        className="sm:col-span-2 relative"
        onBlur={() => { blurTimer.current = setTimeout(() => { cancelSearch(); setOpen(false); setActive(-1); }, 150); }}
        onFocus={() => { if (blurTimer.current) clearTimeout(blurTimer.current); }}
      >
        <Field label="Address line 1">
          <Input
            value={v.address_line1 || ""} onChange={onLine1} onKeyDown={onKeyDown}
            placeholder={publicMode ? "Flat / house no, street" : "Flat / house no, street — start typing to search"}
            disabled={disabled} autoComplete={publicMode ? "address-line1" : "off"}
            data-testid="addr-line1"
            {...(!publicMode ? {
              role: "combobox", "aria-expanded": open,
              "aria-controls": open ? listId : undefined,
              "aria-autocomplete": "list", "aria-busy": busy,
              "aria-activedescendant": open && !busy && active >= 0 ? optId(active) : undefined,
            } : {})}
          />
        </Field>
        {!publicMode && open && (
          <ul id={listId} role="listbox" aria-label="Address suggestions"
              className="absolute z-30 left-0 right-0 mt-1 max-h-60 overflow-y-auto rounded-lg border border-line bg-surface shadow-lg py-1">
            {busy && (
              <li role="presentation" className="flex min-h-[44px] items-center gap-2 px-3 py-2.5 text-sm text-ink-subtle">
                <Spinner aria-hidden="true" className="h-4 w-4 shrink-0" /> Searching addresses…
              </li>
            )}
            {!busy && sugs.length === 0 && (
              <li role="presentation" className="flex min-h-[44px] items-center px-3 py-2.5 text-sm text-ink-subtle">
                {searchError
                  ? "Address search is unavailable — enter the address manually"
                  : "No matches — keep typing or enter the address manually"}
              </li>
            )}
            {!busy && sugs.map((s, i) => (
              <li key={s.place_id || s.osm_id || `${s.label}-${i}`} id={optId(i)} role="option"
                  aria-selected={i === active}
                  onPointerDown={(e) => { e.preventDefault(); pick(s); }}
                  onMouseEnter={() => setActive(i)}
                  className={`flex min-h-[44px] items-center border-l-2 px-3 py-2.5 text-sm cursor-pointer break-words ${
                    i === active ? "bg-muted text-ink font-medium border-primary" : "text-ink-2 border-transparent"}`}>
                {s.label || [s.address_line1, s.city, s.state, s.postal_code].filter(Boolean).join(", ")}
              </li>
            ))}
          </ul>
        )}
      </div>
      <Field label="Address line 2 (optional)">
        <Input value={v.address_line2 || ""} onChange={set("address_line2")} disabled={disabled}
               autoComplete="address-line2" placeholder="Apartment / floor" />
      </Field>
      <Field label="Landmark (optional)">
        <Input value={v.landmark || ""} onChange={set("landmark")} disabled={disabled}
               placeholder="e.g. Near City Mall" data-testid="addr-landmark" />
      </Field>
    </div>
  );
}
